import { Fragment, useMemo, useState } from 'react'
import { ButtonSecondary } from '../../buttons/ButtonSecondary'
import { EmptyState } from '../../reusable/emptyState/EmptyState'
import {
    AnalysisCsvColumn,
    BatchAnalysis,
    Outcome,
    analysisCsvFilename,
    buildAnalysisClientsCsv,
    buildIneligibleBreakdown,
    money,
} from '../viewThruBatchView/BatchAnalysis'
import { AnalysisClientModal } from '../viewThruBatchView/AnalysisClientModal'
import {
    buildOverlapBreakdown,
    clientRejectionReasons,
    reasonLabel,
    secondaryRejectionReasons,
} from '../viewThruBatchView/analysisReasons'
import { EnvironmentBadge, count, downloadCsv, formatDateTime } from '../viewThruBatchView/shared'
import tableStyles from '../viewThruBatchView/runsTable.module.scss'
import pageStyles from '../viewThruBatchView/styles.module.scss'
import { CCJ_RULE_OPTIONS, DEFAULT_CCJ_RULE, ccjRuleLabel } from './ccjRules'
import { BatchFlaggedClient, batchLabel, flagBatchClients, useAllBatchesAnalysis } from './useAllBatchesAnalysis'
import { EMPTY_WAIVERS, ImportState, hasWaivers, useAllBatchesImport } from './useAllBatchesImport'
import { buildWaiverOptions, claimsLetThrough, clientsLetThrough, describeWaivers } from './importWaivers'
import { ImportedReportPanel } from './ImportedReportPanel'
import { buildImportList, buildImportListCsv, importListByOutcome, importListFilename } from './importPreviewExport'
import styles from './styles.module.scss'

const BATCH_CSV_COLUMNS: AnalysisCsvColumn<BatchFlaggedClient>[] = [
    { header: 'batch_id', value: (client) => client.batchId },
    { header: 'batch_file', value: (client) => client.batchLabel },
    { header: 'batch_origin', value: (client) => client.batchOrigin },
]

type ExportScope = 'all' | Outcome

const EXPORT_SCOPES: { scope: ExportScope; label: string }[] = [
    { scope: 'all', label: 'Everything' },
    { scope: 'eligible', label: 'Eligible' },
    { scope: 'ineligible', label: 'Not eligible' },
    { scope: 'duplicate', label: 'Duplicates' },
]

const scopeSuffix: Record<ExportScope, string> = {
    all: 'all-clients',
    eligible: 'eligible',
    ineligible: 'not-eligible',
    duplicate: 'duplicates',
}

const filterScope = (clients: BatchFlaggedClient[], scope: ExportScope) =>
    scope === 'all' ? clients : clients.filter((client) => client.outcome === scope)

const exportClients = (clients: BatchFlaggedClient[], scope: ExportScope, filenameBase: string, withBatchColumns: boolean) => {
    const rows = filterScope(clients, scope)
    if (!rows.length) return
    downloadCsv(
        analysisCsvFilename(filenameBase, scopeSuffix[scope]),
        buildAnalysisClientsCsv(rows, withBatchColumns ? BATCH_CSV_COLUMNS : [])
    )
}

const todayStamp = () => new Date().toISOString().slice(0, 10)

const AnalysisStatusBadge = ({ status }: { status: 'idle' | 'running' | 'complete' | 'failed' }) => {
    if (status === 'idle') return <span className={tableStyles.badge}>Not analysed</span>
    const className =
        status === 'running'
            ? tableStyles.statusRunning
            : status === 'complete'
            ? tableStyles.statusComplete
            : tableStyles.statusFailed
    return (
        <span className={`${tableStyles.badge} ${className}`}>
            {status === 'running' && <span className={tableStyles.statusSpinner} aria-hidden="true" />}
            {status === 'running' ? 'Analysing' : status === 'complete' ? 'Analysed' : 'Failed'}
        </span>
    )
}

const importSummary = (state: ImportState | undefined) => {
    if (!state) return null
    if (state.status === 'queued') return <span className={tableStyles.badge}>Queued</span>
    if (state.status === 'running') {
        const run = state.run
        return (
            <>
                <span className={`${tableStyles.badge} ${tableStyles.statusRunning}`}>
                    <span className={tableStyles.statusSpinner} aria-hidden="true" />
                    {run && run.status === 'running' ? 'Importing' : 'Preparing'}
                </span>
                {run && run.status === 'running' && (
                    <span className={styles.cellMeta}>
                        {Math.min(run.processed + 1, run.total)} of {run.total}
                    </span>
                )}
            </>
        )
    }
    if (state.status === 'complete') {
        const detail = [
            state.run.errors ? `${state.run.errors} failed` : '',
            state.run.blocked ? `${state.run.blocked} blocked` : '',
        ].filter(Boolean)
        return (
            <>
                <span className={`${tableStyles.badge} ${state.run.errors ? tableStyles.statusFailed : tableStyles.statusComplete}`}>
                    {state.run.imported} imported
                </span>
                {detail.length > 0 && <span className={styles.cellMeta}>{detail.join(' · ')}</span>}
            </>
        )
    }
    return (
        <>
            <span className={`${tableStyles.badge} ${tableStyles.statusFailed}`} title={state.error}>
                Failed
            </span>
            {state.run?.imported ? <span className={styles.cellMeta}>{state.run.imported} imported first</span> : null}
        </>
    )
}

/**
 * Fixed column plan so the table always fits its container. The batch column
 * has no width and absorbs whatever is left; text in it truncates.
 */
const BATCH_COLUMNS: { key: string; width?: number }[] = [
    { key: 'check', width: 36 },
    { key: 'batch' },
    { key: 'analysis', width: 132 },
    { key: 'clients', width: 72 },
    { key: 'eligible', width: 78 },
    { key: 'ineligible', width: 112 },
    { key: 'import', width: 118 },
    { key: 'actions', width: 214 },
]

const PREVIEW_COLUMNS: { key: string; width?: number }[] = [
    { key: 'batch' },
    { key: 'ready', width: 84 },
    { key: 'waived', width: 84 },
    { key: 'blocked', width: 84 },
    { key: 'already', width: 110 },
    { key: 'unmatched', width: 110 },
    { key: 'import', width: 140 },
    { key: 'actions', width: 112 },
]

const ColumnPlan = ({ columns }: { columns: { key: string; width?: number }[] }) => (
    <colgroup>
        {columns.map((column) => (
            <col key={column.key} style={column.width ? { width: column.width } : undefined} />
        ))}
    </colgroup>
)

export const ViewThruAllBatchesView = () => {
    const analysis = useAllBatchesAnalysis()
    // The import previews and runs under the same CCJ rule the batches were analysed with.
    const importer = useAllBatchesImport({ ccjRule: analysis.ccjRule })
    const [expanded, setExpanded] = useState<string | null>(null)
    const [reasonFilter, setReasonFilterState] = useState<string | null>(null)
    // A second reason the listed clients must *also* have failed on.
    const [overlapFilter, setOverlapFilter] = useState<string | null>(null)
    const [openClient, setOpenClient] = useState<BatchFlaggedClient | null>(null)

    const setReasonFilter = (reason: string | null) => {
        setReasonFilterState(reason)
        setOverlapFilter(null)
    }

    const selectedBatches = useMemo(
        () => analysis.batches.filter((run) => analysis.selected.has(run.id)),
        [analysis.batches, analysis.selected]
    )
    const selectedIds = selectedBatches.map((run) => run.id)
    const allSelected = analysis.batches.length > 0 && selectedIds.length === analysis.batches.length

    const { analysed } = analysis
    const totals = useMemo(() => {
        const sum = (pick: (entry: (typeof analysed)[number]) => number) =>
            analysed.reduce((total, entry) => total + (pick(entry) || 0), 0)
        return {
            batches: analysed.length,
            clients: sum(({ analysis: a }) => a.counts.clients),
            eligible: sum(({ analysis: a }) => a.counts.eligibleClients),
            ineligible: sum(({ analysis: a }) => a.counts.ineligibleClients),
            duplicates: sum(({ analysis: a }) => a.counts.duplicateClients ?? a.counts.notEligibleDups),
            eligibleAgreements: sum(({ analysis: a }) => a.counts.eligibleAgreements),
            bookValue: sum(({ analysis: a }) => a.summary?.totalBookValue || 0),
        }
    }, [analysed])

    const ineligibleClients = useMemo(
        () => analysis.allClients.filter((client) => client.outcome === 'ineligible'),
        [analysis.allClients]
    )
    const ineligibleBreakdown = useMemo(() => buildIneligibleBreakdown(ineligibleClients), [ineligibleClients])
    const reasonClients = useMemo(
        () =>
            reasonFilter
                ? ineligibleClients.filter((client) => (client.clientRejectionReason || 'unspecified') === reasonFilter)
                : [],
        [ineligibleClients, reasonFilter]
    )
    const overlaps = useMemo(
        () => (reasonFilter ? buildOverlapBreakdown(ineligibleClients, reasonFilter) : { entries: [], withOverlaps: 0 }),
        [ineligibleClients, reasonFilter]
    )
    const filteredIneligible = useMemo(
        () =>
            overlapFilter
                ? reasonClients.filter((client) => clientRejectionReasons(client).includes(overlapFilter))
                : reasonClients,
        [reasonClients, overlapFilter]
    )

    const previewTargets = selectedIds
    const importTargets = selectedIds.filter((id) => {
        const state = importer.previews[id]
        return state?.status === 'complete' && Number(state.preview.counts?.importable || 0) > 0
    })
    const previewsReady =
        previewTargets.length > 0 && previewTargets.every((id) => importer.previews[id]?.status === 'complete')
    const previewTotals = previewTargets.reduce(
        (acc, id) => {
            const state = importer.previews[id]
            if (state?.status !== 'complete') return acc
            const counts = state.preview.counts || {}
            acc.importable += Number(counts.importable || 0)
            acc.waivedClients += Number(counts.waivedClients || 0)
            acc.waivedClaims += Number(counts.waivedClaims || 0)
            acc.blocked += Number(counts.blocked || 0)
            acc.alreadyImported += Number(counts.alreadyImported || 0)
            acc.unmatchedFiles += Number(counts.unmatchedFiles || 0)
            return acc
        },
        { importable: 0, waivedClients: 0, waivedClaims: 0, blocked: 0, alreadyImported: 0, unmatchedFiles: 0 }
    )
    const importBusy = importer.importing || importer.previewing

    // The final word on who goes in: every client the completed previews of the
    // selected batches account for, split into importing and not importing.
    const importList = useMemo(() => buildImportList(selectedBatches, importer.previews), [importer.previews, selectedBatches])
    const importingRows = useMemo(() => importListByOutcome(importList, 'importing'), [importList])
    const notImportingRows = useMemo(() => importListByOutcome(importList, 'not_importing'), [importList])
    const previewedCount = previewTargets.filter((id) => importer.previews[id]?.status === 'complete').length
    const exportImportList = (outcome: 'importing' | 'not_importing') => {
        const rows = outcome === 'importing' ? importingRows : notImportingRows
        if (!rows.length) return
        const single = previewedCount === 1 ? selectedBatches.find((run) => importer.previews[run.id]?.status === 'complete') : null
        downloadCsv(importListFilename(outcome, todayStamp(), single ? batchLabel(single) : undefined), buildImportListCsv(rows))
    }

    const { waivers } = importer
    const waiving = hasWaivers(waivers)
    const hasAnalysis = analysis.analysed.length > 0
    const [showAllChecks, setShowAllChecks] = useState(false)
    const waiverOptions = useMemo(() => buildWaiverOptions(analysis.allClients), [analysis.allClients])
    const waiverEstimate = useMemo(
        () => ({
            clients: clientsLetThrough(analysis.allClients, waivers).length,
            claims: claimsLetThrough(analysis.allClients, waivers),
        }),
        [analysis.allClients, waivers]
    )
    const waivedCount = waivers.client.length + waivers.claim.length

    const startImport = () => {
        if (!importTargets.length) return
        const batches = `${importTargets.length} ${importTargets.length === 1 ? 'batch' : 'batches'}`
        const gated = waiving
            ? `Import ${previewTotals.importable} clients from ${batches} into Sentinel Reporting?\n\n` +
              `${previewTotals.waivedClients} of them only pass because these checks are waived: ${describeWaivers(
                  waivers
              )}.${
                  previewTotals.waivedClaims ? ` ${previewTotals.waivedClaims} agreements are included under a waived check.` : ''
              }`
            : `Import ${previewTotals.importable} eligible, duplicate-free clients from ${batches} into Sentinel Reporting?`
        const rule =
            analysis.ccjRule === DEFAULT_CCJ_RULE
                ? ''
                : `\n\nCCJ rule: ${ccjRuleLabel(analysis.ccjRule)}. Clients with 1–4 CCJs are imported; the funding batch will not collect them while Sentinel Reporting keeps its zero-CCJ rule.`
        if (
            !window.confirm(
                `${gated}${rule}\n\nSignature dates are adjusted and signature images are made transparent as each client is imported.`
            )
        ) {
            return
        }
        void importer.importBatches(importTargets)
    }

    return (
        <div className={`${pageStyles.page} ${styles.page}`}>
            <section className={tableStyles.analysisSection} aria-busy={analysis.running}>
                <div className={tableStyles.analysisHeading}>
                    <div>
                        <h3>Eligibility across all batches</h3>
                        <p>
                            Runs the same eligibility analysis as each batch page — identity, industry search,
                            deceased and bankruptcy screening, the FairClaim agreement rules, duplicate deduction and
                            the funding engine — over every completed batch, so results can be compared and exported
                            together or one batch at a time.
                        </p>
                    </div>
                    <div className={styles.toolbar}>
                        <ButtonSecondary onClick={() => analysis.selectAll(!allSelected)} disabled={analysis.running}>
                            {allSelected ? 'Select none' : 'Select all'}
                        </ButtonSecondary>
                        <ButtonSecondary onClick={() => void analysis.reload()} disabled={analysis.loading || analysis.running}>
                            Refresh batches
                        </ButtonSecondary>
                        {analysis.running ? (
                            <button type="button" className={styles.primaryButton} onClick={analysis.cancel}>
                                Stop after current batches
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={styles.primaryButton}
                                onClick={() => void analysis.analyseSelected()}
                                disabled={!selectedIds.length || analysis.loading}
                            >
                                Analyse {selectedIds.length} selected {selectedIds.length === 1 ? 'batch' : 'batches'}
                            </button>
                        )}
                    </div>
                </div>

                <fieldset
                    className={`${styles.rulePanel} ${analysis.ccjRule !== DEFAULT_CCJ_RULE ? styles.rulePanelActive : ''}`}
                    disabled={analysis.running}
                >
                    <legend>CCJ rule</legend>
                    <div className={styles.ruleOptions} role="radiogroup" aria-label="CCJ rule">
                        {CCJ_RULE_OPTIONS.map((option) => (
                            <label
                                key={option.key}
                                className={`${styles.ruleOption} ${analysis.ccjRule === option.key ? styles.ruleChecked : ''}`}
                            >
                                <input
                                    type="radio"
                                    name="ccj-rule"
                                    value={option.key}
                                    checked={analysis.ccjRule === option.key}
                                    onChange={() => analysis.setCcjRule(option.key)}
                                />
                                <span>
                                    <strong>{option.label}</strong>
                                    <small>{option.help}</small>
                                </span>
                            </label>
                        ))}
                    </div>
                    <p className={styles.ruleHelp}>
                        Applied to the analysis and to the import previews and runs below. Switching rules clears the
                        current results and previews, so the batches must be analysed again.
                        {analysis.ccjRule === 'maxFour' && (
                            <>
                                {' '}
                                Note: the funding batch still applies the zero-CCJ rule to Sentinel Reporting, so clients
                                imported with 1–4 CCJs will not be collected into a funding batch until that tolerance is
                                changed.
                            </>
                        )}
                    </p>
                </fieldset>

                {(analysis.running || analysis.progress.done > 0) && (
                    <div className={tableStyles.importProgress} role="status" aria-live="polite">
                        <div>
                            <strong>
                                {analysis.running
                                    ? `Analysing batch ${Math.min(analysis.progress.done + 1, analysis.progress.total)} of ${
                                          analysis.progress.total
                                      }`
                                    : `Analysed ${analysis.progress.done} of ${analysis.progress.total} selected`}
                            </strong>
                            <span>
                                {analysis.progress.done - analysis.progress.failed} analysed · {analysis.progress.failed} failed
                            </span>
                        </div>
                        <progress max={Math.max(analysis.progress.total, 1)} value={analysis.progress.done} />
                    </div>
                )}

                {analysis.analysed.length > 0 && (
                    <>
                        <div className={tableStyles.analysisSummary}>
                            {(
                                [
                                    ['Batches analysed', String(totals.batches)],
                                    ['Clients', String(totals.clients)],
                                    ['Eligible', String(totals.eligible)],
                                    ['Not eligible', String(totals.ineligible + totals.duplicates)],
                                    ['Of which duplicates', String(totals.duplicates)],
                                    ['Eligible agreements', String(totals.eligibleAgreements)],
                                    ['Eligible book value', money(totals.bookValue)],
                                ] as [string, string][]
                            ).map(([label, value]) => (
                                <div key={label} className={tableStyles.summaryCard} role="group" aria-label={label}>
                                    <span className={tableStyles.detailLabel}>{label}</span>
                                    <span className={tableStyles.detailValue}>{value}</span>
                                </div>
                            ))}
                        </div>

                        <div className={styles.exportRow}>
                            <span className={tableStyles.detailLabel}>Export across analysed batches</span>
                            <div className={tableStyles.analysisExports}>
                                {EXPORT_SCOPES.map(({ scope, label }) => {
                                    const n = filterScope(analysis.allClients, scope).length
                                    return (
                                        <button
                                            key={scope}
                                            type="button"
                                            disabled={!n}
                                            onClick={() =>
                                                exportClients(analysis.allClients, scope, `viewthru-all-batches-${todayStamp()}`, true)
                                            }
                                        >
                                            {label} CSV ({n})
                                        </button>
                                    )
                                })}
                            </div>
                        </div>

                        {ineligibleBreakdown.length > 0 && (
                            <div className={`${tableStyles.duplicateBreakdown} ${tableStyles.ineligibleBreakdown}`}>
                                <strong>Not eligible breakdown — all analysed batches</strong>
                                <p>Why {ineligibleClients.length} clients were rejected. Click a reason to list those clients.</p>
                                <div className={tableStyles.ineligibleBreakdownGroups}>
                                    {ineligibleBreakdown.map((section) => (
                                        <div key={section.group}>
                                            <h5>{section.group}</h5>
                                            {section.entries.map((entry) => (
                                                <button
                                                    key={entry.key}
                                                    type="button"
                                                    className={reasonFilter === entry.key ? tableStyles.duplicateBreakdownActive : ''}
                                                    onClick={() => setReasonFilter(reasonFilter === entry.key ? null : entry.key)}
                                                    aria-pressed={reasonFilter === entry.key}
                                                >
                                                    <span>
                                                        {entry.label}
                                                        {entry.detail && <small>{entry.detail}</small>}
                                                    </span>
                                                    <span>{entry.count}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                                {reasonFilter && (
                                    <div className={styles.reasonClients}>
                                        <div className={styles.reasonClientsHeader}>
                                            <span>
                                                {filteredIneligible.length} {filteredIneligible.length === 1 ? 'client' : 'clients'}
                                                {overlapFilter && (
                                                    <>
                                                        {' '}
                                                        who also failed <em>{reasonLabel(overlapFilter)}</em>
                                                    </>
                                                )}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    exportClients(
                                                        filteredIneligible,
                                                        'all',
                                                        `viewthru-not-eligible-${reasonFilter}${overlapFilter ? `-and-${overlapFilter}` : ''}-${todayStamp()}`,
                                                        true
                                                    )
                                                }
                                            >
                                                Export these clients
                                            </button>
                                            {overlapFilter && (
                                                <button type="button" onClick={() => setOverlapFilter(null)}>
                                                    Show all {reasonClients.length}
                                                </button>
                                            )}
                                            <button type="button" onClick={() => setReasonFilter(null)}>
                                                Clear
                                            </button>
                                        </div>

                                        {overlaps.entries.length > 0 && (
                                            <details
                                                className={styles.overlaps}
                                                open={Boolean(overlapFilter)}
                                                aria-label="Overlapping failures"
                                            >
                                                <summary>
                                                    Overlapping failures
                                                    <small>
                                                        {overlaps.withOverlaps} of {reasonClients.length} also failed at least one
                                                        other check
                                                    </small>
                                                </summary>
                                                <p>
                                                    These clients are counted under <strong>{reasonLabel(reasonFilter)}</strong>{' '}
                                                    because it is the first check that rejected them. Pick another check to
                                                    narrow the list to clients who failed both.
                                                </p>
                                                <div className={styles.overlapList}>
                                                    {overlaps.entries.map((entry) => (
                                                        <button
                                                            key={entry.key}
                                                            type="button"
                                                            className={overlapFilter === entry.key ? styles.overlapActive : ''}
                                                            aria-pressed={overlapFilter === entry.key}
                                                            onClick={() =>
                                                                setOverlapFilter(overlapFilter === entry.key ? null : entry.key)
                                                            }
                                                        >
                                                            <span>{entry.label}</span>
                                                            <span>{entry.count}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </details>
                                        )}

                                        <div className={tableStyles.tableWrapper}>
                                            <table className={`${tableStyles.runsTable} ${styles.batchTable} ${styles.clientTable}`}>
                                                <colgroup>
                                                    <col />
                                                    <col style={{ width: 220 }} />
                                                    <col />
                                                    <col style={{ width: 120 }} />
                                                    <col style={{ width: 90 }} />
                                                    <col style={{ width: 96 }} />
                                                    <col />
                                                </colgroup>
                                                <thead>
                                                    <tr>
                                                        <th>Client</th>
                                                        <th>Email</th>
                                                        <th>Batch</th>
                                                        <th>Origin</th>
                                                        <th className={styles.numCell}>Agreements</th>
                                                        <th className={styles.numCell}>Value</th>
                                                        <th>Also failed</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredIneligible.slice(0, 200).map((client) => {
                                                        const others = secondaryRejectionReasons(client)
                                                        const open = () => setOpenClient(client)
                                                        return (
                                                            <tr
                                                                key={`${client.batchId}:${client.clientKey}`}
                                                                className={styles.clientRow}
                                                                onClick={open}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                                        event.preventDefault()
                                                                        open()
                                                                    }
                                                                }}
                                                                tabIndex={0}
                                                                role="button"
                                                                aria-label={`Open ${client.name || 'client'} details and PDFs`}
                                                                title="Open client data and PDF reports"
                                                            >
                                                                <td>
                                                                    <span className={styles.clientName}>{client.name || 'Unnamed client'}</span>
                                                                </td>
                                                                <td title={client.email || undefined}>{client.email || '—'}</td>
                                                                <td className={tableStyles.fileCell} title={client.batchLabel}>
                                                                    {client.batchLabel}
                                                                </td>
                                                                <td title={client.batchOrigin || undefined}>{client.batchOrigin || '—'}</td>
                                                                <td className={styles.numCell}>
                                                                    {client.claimCount ?? client.claims?.length ?? 0}
                                                                </td>
                                                                <td className={styles.numCell}>{money(client.clientTotalValue)}</td>
                                                                <td className={styles.alsoFailedCell}>
                                                                    {others.length ? (
                                                                        others.map((reason) => (
                                                                            <span key={reason} className={styles.reasonChip}>
                                                                                {reasonLabel(reason)}
                                                                            </span>
                                                                        ))
                                                                    ) : (
                                                                        <span className={styles.numMuted}>—</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        <p className={tableStyles.tableFootnote}>
                                            Click a client to see their data and open the credit and AML PDFs.
                                            {filteredIneligible.length > 200 && ' Showing the first 200 — export for the full list.'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>

            <div className={tableStyles.section}>
                <div className={`${tableStyles.sectionHeader} ${tableStyles.sectionHeaderPlain}`}>
                    <h2>Completed batches</h2>
                    <span className={styles.countPill}>
                        {selectedIds.length} of {analysis.batches.length} selected
                    </span>
                    {analysis.analysed.length > 0 && (
                        <span className={styles.headerHint}>
                            {analysis.analysed.length} analysed · click <strong>Details</strong> on a row for the full
                            batch breakdown and single-batch import
                        </span>
                    )}
                </div>
                <div className={tableStyles.tableWrapper}>
                    {analysis.loadError ? (
                        <EmptyState
                            bare
                            tone="error"
                            title="Could not load batches"
                            description={analysis.loadError}
                            action={<ButtonSecondary onClick={() => void analysis.reload()}>Try again</ButtonSecondary>}
                        />
                    ) : analysis.loading && !analysis.batches.length ? (
                        <EmptyState bare tone="loading" title="Loading batches" />
                    ) : !analysis.batches.length ? (
                        <EmptyState bare title="No completed batches" description="Only batches that finished processing can be analysed." />
                    ) : (
                        <table className={`${tableStyles.runsTable} ${styles.batchTable}`}>
                            <ColumnPlan columns={BATCH_COLUMNS} />
                            <thead>
                                <tr>
                                    <th className={styles.checkCell}>
                                        <input
                                            type="checkbox"
                                            aria-label="Select all batches"
                                            checked={allSelected}
                                            onChange={(event) => analysis.selectAll(event.target.checked)}
                                            disabled={analysis.running}
                                        />
                                    </th>
                                    <th>Batch</th>
                                    <th>Analysis</th>
                                    <th className={styles.numCell}>Clients</th>
                                    <th className={styles.numCell}>Eligible</th>
                                    <th className={styles.numCell} title="Total rejected, including duplicates">
                                        Not eligible
                                    </th>
                                    <th>Import</th>
                                    <th className={styles.actionsHead}>
                                        <span className={styles.srOnly}>Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysis.batches.map((run) => {
                                    const state = analysis.states[run.id] || { status: 'idle' as const }
                                    const result = state.status === 'complete' ? state.analysis : null
                                    const isExpanded = expanded === run.id
                                    const isSelected = analysis.selected.has(run.id)
                                    const batchClients = result ? flagBatchClients(run, result) : []
                                    const duplicates = result ? result.counts.duplicateClients ?? result.counts.notEligibleDups ?? 0 : null
                                    // Duplicates are rejections too, so the headline is everything not eligible
                                    // and the sub-line says how much of that was duplicate deduction.
                                    const notEligibleTotal = result ? result.counts.ineligibleClients + (duplicates || 0) : null
                                    const rowClass = [
                                        isSelected ? styles.selectedRow : '',
                                        isExpanded ? styles.expandedRow : '',
                                        state.status === 'running' ? styles.busyRow : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')
                                    return (
                                        <Fragment key={run.id}>
                                            <tr className={rowClass || undefined}>
                                                <td className={styles.checkCell}>
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Select ${batchLabel(run)}`}
                                                        checked={isSelected}
                                                        onChange={() => analysis.toggle(run.id)}
                                                        disabled={analysis.running}
                                                    />
                                                </td>
                                                <td className={styles.batchCell}>
                                                    <div className={styles.batchCellInner}>
                                                        <span className={styles.batchName} title={run.filename || run.id}>
                                                            {run.filename || run.id}
                                                        </span>
                                                        <span className={styles.batchOrigin} title={run.origin || undefined}>
                                                            {run.origin || 'No origin'}
                                                        </span>
                                                        <span className={styles.batchMeta}>
                                                            <span>{formatDateTime(run.created_at)}</span>
                                                            <span className={styles.metaBadge}>
                                                                <EnvironmentBadge run={run} />
                                                            </span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className={styles.statusCell}>
                                                    <div
                                                        className={styles.statusCellInner}
                                                        title={
                                                            state.status === 'complete'
                                                                ? `Analysed ${formatDateTime(state.analysedAt.toISOString())}`
                                                                : undefined
                                                        }
                                                    >
                                                        <AnalysisStatusBadge status={state.status} />
                                                        {state.status === 'failed' && (
                                                            <div className={styles.cellError} title={state.error}>
                                                                {state.error}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className={styles.numCell}>
                                                    {result ? result.counts.clients : count(run.success_count)}
                                                </td>
                                                <td className={`${styles.numCell} ${result ? styles.numEligible : ''}`}>
                                                    {result
                                                        ? result.counts.eligibleClients
                                                        : run.eligible_count == null
                                                        ? <span className={styles.numMuted}>—</span>
                                                        : count(run.eligible_count)}
                                                </td>
                                                <td className={styles.numCell}>
                                                    {result ? (
                                                        <>
                                                            <span className={notEligibleTotal ? styles.numIneligible : undefined}>
                                                                {notEligibleTotal}
                                                            </span>
                                                            <span className={`${styles.cellMeta} ${duplicates ? styles.numDuplicate : ''}`}>
                                                                {duplicates || 0} duplicate{duplicates === 1 ? '' : 's'}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className={styles.numMuted}>—</span>
                                                    )}
                                                </td>
                                                <td className={styles.statusCell}>
                                                    {importSummary(importer.imports[run.id]) || <span className={styles.numMuted}>—</span>}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    <div className={styles.rowActions}>
                                                        <button
                                                            type="button"
                                                            className={styles.rowButton}
                                                            onClick={() => void analysis.analyseOne(run.id)}
                                                            disabled={state.status === 'running'}
                                                        >
                                                            {state.status === 'running' ? 'Analysing…' : result ? 'Re-run' : 'Analyse'}
                                                        </button>
                                                        {result && (
                                                            <>
                                                                <details className={styles.exportMenu}>
                                                                    <summary
                                                                        className={styles.rowButton}
                                                                        aria-label={`Export ${batchLabel(run)}`}
                                                                    >
                                                                        Export
                                                                        <span className={styles.caret} aria-hidden="true" />
                                                                    </summary>
                                                                    <div className={styles.exportMenuList} role="menu">
                                                                        {EXPORT_SCOPES.map(({ scope, label }) => {
                                                                            const n = filterScope(batchClients, scope).length
                                                                            return (
                                                                                <button
                                                                                    key={scope}
                                                                                    type="button"
                                                                                    role="menuitem"
                                                                                    disabled={!n}
                                                                                    onClick={(event) => {
                                                                                        exportClients(batchClients, scope, batchLabel(run), false)
                                                                                        event.currentTarget.closest('details')?.removeAttribute('open')
                                                                                    }}
                                                                                >
                                                                                    <span>{label}</span>
                                                                                    <span>{n}</span>
                                                                                </button>
                                                                            )
                                                                        })}
                                                                    </div>
                                                                </details>
                                                                <button
                                                                    type="button"
                                                                    className={`${styles.rowButton} ${isExpanded ? styles.rowButtonActive : ''}`}
                                                                    onClick={() => setExpanded(isExpanded ? null : run.id)}
                                                                    aria-expanded={isExpanded}
                                                                >
                                                                    {isExpanded ? 'Hide' : 'Details'}
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && result && (
                                                <tr className={styles.detailRow}>
                                                    <td colSpan={BATCH_COLUMNS.length}>
                                                        <div className={styles.detailInner}>
                                                            <BatchAnalysis
                                                                analysis={result}
                                                                batchId={run.id}
                                                                analysedAt={state.status === 'complete' ? state.analysedAt : null}
                                                                filenameBase={batchLabel(run)}
                                                                onRerun={() => analysis.analyseOne(run.id)}
                                                                rerunning={false}
                                                                onAnalysisRefresh={() => analysis.analyseOne(run.id)}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <section className={tableStyles.importPanel} aria-labelledby="viewthru-import-all-title" aria-busy={importBusy}>
                <div>
                    <h4 id="viewthru-import-all-title">Import eligible clients from the selected batches</h4>
                    <p>
                        Previews every selected batch against the signature text files below, then imports them one
                        batch at a time with the full import rules: screening and funding gates re-checked on write,
                        duplicate and already-imported clients skipped, signature dates adjusted and signature images
                        made transparent. Anyone imported from an earlier batch is skipped by the next. Eligibility
                        checks can be waived below to let specific failures through; the preview shows who that
                        adds before anything is written.
                    </p>
                </div>
                <div className={tableStyles.importFields}>
                    <label>
                        <span className={tableStyles.detailLabel}>Bucket</span>
                        <input value="sentinelgeneratedfiles" disabled aria-label="Bucket" />
                    </label>
                    <label>
                        <span className={tableStyles.detailLabel}>Data path</span>
                        <input
                            value={importer.dataPath}
                            onChange={(event) => importer.setDataPath(event.target.value)}
                            placeholder="Path to generated data"
                            disabled={importBusy}
                            required
                        />
                    </label>
                    <label>
                        <span className={tableStyles.detailLabel}>Subdirectory (optional)</span>
                        <input
                            value={importer.subdirectory}
                            onChange={(event) => importer.setSubdirectory(event.target.value)}
                            placeholder="Leave blank to scan everything below"
                            disabled={importBusy}
                        />
                    </label>
                    <button
                        type="button"
                        onClick={() => void importer.previewBatches(previewTargets)}
                        disabled={importBusy || !previewTargets.length || !importer.dataPath.trim()}
                    >
                        {importer.previewing
                            ? 'Previewing…'
                            : `Preview import for ${previewTargets.length} ${previewTargets.length === 1 ? 'batch' : 'batches'}`}
                    </button>
                </div>

                <fieldset
                    className={`${styles.waiverPanel} ${waiving ? styles.waiverPanelActive : ''}`}
                    disabled={importBusy}
                    aria-describedby="viewthru-waiver-help"
                >
                    <legend>Eligibility checks to waive</legend>
                    <div className={styles.waiverIntro}>
                        <p id="viewthru-waiver-help" className={styles.waiverHelp}>
                            Tick a check to let clients and agreements through the import despite failing it. A
                            client is only let through when <strong>every</strong> check recorded against them is
                            ticked — one unticked failure still blocks them. Signature-file matching, duplicate
                            deduction and the already-imported check always apply.
                        </p>
                        {hasAnalysis && (
                            <label className={styles.waiverToggle}>
                                <input
                                    type="checkbox"
                                    checked={showAllChecks}
                                    onChange={(event) => setShowAllChecks(event.target.checked)}
                                />
                                Show checks nobody fails
                            </label>
                        )}
                    </div>
                    <div className={styles.waiverGroups}>
                        {(
                            [
                                { kind: 'client', title: 'Client checks', options: waiverOptions.client, unit: 'clients' },
                                { kind: 'claim', title: 'Agreement checks', options: waiverOptions.claim, unit: 'agreements' },
                            ] as const
                        ).map((group) => {
                            const selected = waivers[group.kind]
                            const failing = group.options.filter((option) => option.count > 0)
                            // Once analysed, only the checks actually holding someone back are
                            // shown (plus anything already ticked) unless the user asks for all.
                            const visible =
                                hasAnalysis && !showAllChecks
                                    ? group.options.filter((option) => option.count > 0 || selected.includes(option.key))
                                    : group.options
                            const hidden = group.options.length - visible.length
                            return (
                                <section key={group.kind} className={styles.waiverGroup} aria-label={group.title}>
                                    <header className={styles.waiverGroupHeader}>
                                        <h5>{group.title}</h5>
                                        <span className={styles.waiverGroupMeta}>
                                            {selected.length ? (
                                                <strong>
                                                    {selected.length} of {group.options.length} waived
                                                </strong>
                                            ) : (
                                                `${group.options.length} checks`
                                            )}
                                            {hasAnalysis && (
                                                <>
                                                    {' · '}
                                                    {failing.length
                                                        ? `${failing.length} with failing ${group.unit}`
                                                        : `no ${group.unit} failing`}
                                                </>
                                            )}
                                        </span>
                                        {selected.length > 0 && (
                                            <button
                                                type="button"
                                                className={styles.linkButton}
                                                onClick={() => importer.setWaivers({ ...waivers, [group.kind]: [] })}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </header>
                                    {visible.length ? (
                                        <div className={styles.waiverList}>
                                            {visible.map((option) => {
                                                const checked = selected.includes(option.key)
                                                return (
                                                    <label
                                                        key={option.key}
                                                        className={`${styles.waiverItem} ${checked ? styles.waiverChecked : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => importer.toggleWaiver(group.kind, option.key)}
                                                        />
                                                        <span className={styles.waiverLabel}>{option.label}</span>
                                                        <span
                                                            className={`${styles.waiverCount} ${
                                                                option.count > 0 ? styles.waiverCountFailing : ''
                                                            }`}
                                                            title={
                                                                hasAnalysis
                                                                    ? `${option.count} ${group.unit} in the analysed batches fail this check`
                                                                    : 'Analyse batches to see how many fail this check'
                                                            }
                                                        >
                                                            {hasAnalysis ? count(option.count) : '—'}
                                                        </span>
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    ) : (
                                        <p className={styles.waiverEmpty}>
                                            No {group.unit} in the analysed batches fail any of these checks.
                                        </p>
                                    )}
                                    {hidden > 0 && (
                                        <p className={styles.waiverHidden}>
                                            {hidden} {hidden === 1 ? 'check' : 'checks'} nobody fails hidden.
                                        </p>
                                    )}
                                </section>
                            )
                        })}
                    </div>
                    <div className={styles.waiverSummary} role="status" aria-label="Waived checks summary">
                        {waiving ? (
                            <>
                                <strong>
                                    {waivedCount} {waivedCount === 1 ? 'check' : 'checks'} waived.
                                </strong>{' '}
                                {analysis.analysed.length ? (
                                    <>
                                        In the analysed batches this lets through{' '}
                                        <strong>{count(waiverEstimate.clients)}</strong> not-eligible{' '}
                                        {waiverEstimate.clients === 1 ? 'client' : 'clients'}
                                        {waiverEstimate.claims ? (
                                            <>
                                                {' '}
                                                and <strong>{count(waiverEstimate.claims)}</strong> rejected{' '}
                                                {waiverEstimate.claims === 1 ? 'agreement' : 'agreements'}
                                            </>
                                        ) : null}
                                        , before signature matching and duplicate checks. Preview again to see who is
                                        importable.
                                    </>
                                ) : (
                                    'Preview the batches to see who this lets through.'
                                )}{' '}
                                <button
                                    type="button"
                                    className={styles.linkButton}
                                    onClick={() => importer.setWaivers(EMPTY_WAIVERS)}
                                >
                                    Clear waivers
                                </button>
                            </>
                        ) : (
                            'No checks waived — the import is fully gated.'
                        )}
                    </div>
                </fieldset>

                {importer.message && (
                    <p
                        className={importer.message.tone === 'error' ? tableStyles.detailError : tableStyles.importSuccess}
                        role={importer.message.tone === 'error' ? 'alert' : 'status'}
                    >
                        {importer.message.text}
                    </p>
                )}

                {(Object.keys(importer.previews).length > 0 || Object.keys(importer.imports).length > 0) && (
                    <div className={tableStyles.importPreview}>
                        <div className={tableStyles.importTotals}>
                            <span>
                                <strong>{previewTotals.importable}</strong> ready to import
                            </span>
                            {(waiving || previewTotals.waivedClients > 0) && (
                                <span className={styles.waivedTotal}>
                                    <strong>{previewTotals.waivedClients}</strong> let through by waived checks
                                    {previewTotals.waivedClaims
                                        ? ` (${previewTotals.waivedClaims} ${previewTotals.waivedClaims === 1 ? 'agreement' : 'agreements'})`
                                        : ''}
                                </span>
                            )}
                            <span>
                                <strong>{previewTotals.blocked}</strong> blocked
                            </span>
                            <span>
                                <strong>{previewTotals.alreadyImported}</strong> already imported
                            </span>
                            <span>
                                <strong>{previewTotals.unmatchedFiles}</strong> unmatched files
                            </span>
                        </div>
                        <div className={`${styles.exportRow} ${styles.importListExports}`}>
                            <div>
                                <span className={tableStyles.detailLabel}>Export the import list</span>
                                <p className={styles.importListHelp}>
                                    For bookkeeping: batch, name, email and date of birth of everyone the import will and
                                    will not write, from the previews under the waived checks above. Covers {previewedCount}{' '}
                                    previewed {previewedCount === 1 ? 'batch' : 'batches'}
                                    {previewedCount < previewTargets.length ? ` of ${previewTargets.length} selected` : ''}.
                                </p>
                            </div>
                            <div className={tableStyles.analysisExports}>
                                <button
                                    type="button"
                                    disabled={!importingRows.length}
                                    onClick={() => exportImportList('importing')}
                                    title="Every client the import will write into Sentinel Reporting"
                                >
                                    Importing CSV ({importingRows.length})
                                </button>
                                <button
                                    type="button"
                                    className={styles.reportSecondaryExport}
                                    disabled={!notImportingRows.length}
                                    onClick={() => exportImportList('not_importing')}
                                    title="Every client in the previewed batches the import will not write — blocked, already imported, not eligible or duplicate"
                                >
                                    Not importing CSV ({notImportingRows.length})
                                </button>
                            </div>
                        </div>
                        <div className={tableStyles.tableWrapper}>
                            <table className={`${tableStyles.runsTable} ${styles.batchTable}`}>
                                <ColumnPlan columns={PREVIEW_COLUMNS} />
                                <thead>
                                    <tr>
                                        <th>Batch</th>
                                        <th className={styles.numCell}>Ready</th>
                                        <th className={styles.numCell} title="Ready only because a waived check was ignored">
                                            Waived
                                        </th>
                                        <th className={styles.numCell}>Blocked</th>
                                        <th className={styles.numCell}>Already imported</th>
                                        <th className={styles.numCell}>Unmatched files</th>
                                        <th>Import</th>
                                        <th className={styles.actionsHead}>
                                            <span className={styles.srOnly}>Actions</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedBatches.map((run) => {
                                        const state = importer.previews[run.id]
                                        const counts = state?.status === 'complete' ? state.preview.counts || {} : null
                                        const previewLabel =
                                            state?.status === 'running'
                                                ? 'Previewing…'
                                                : state?.status === 'failed'
                                                ? 'Retry preview'
                                                : state?.status === 'complete'
                                                ? 'Re-preview'
                                                : 'Preview'
                                        const cell = (key: string, tone?: string) =>
                                            counts ? (
                                                <span className={Number(counts[key] || 0) && tone ? tone : undefined}>{counts[key] || 0}</span>
                                            ) : (
                                                <span className={styles.numMuted}>—</span>
                                            )
                                        return (
                                            <tr key={run.id}>
                                                <td className={styles.batchCell}>
                                                    <div className={styles.batchCellInner}>
                                                        <span className={styles.batchName} title={batchLabel(run)}>
                                                            {batchLabel(run)}
                                                        </span>
                                                        <span className={styles.batchMeta}>
                                                            <span className={styles.batchOrigin}>{run.origin || 'No origin'}</span>
                                                            {state?.status === 'running' && <span>Previewing…</span>}
                                                            {state?.status === 'failed' && (
                                                                <span className={styles.inlineError} title={state.error}>
                                                                    {state.error}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className={styles.numCell}>{cell('importable', styles.numEligible)}</td>
                                                <td className={styles.numCell}>{cell('waivedClients', styles.numDuplicate)}</td>
                                                <td className={styles.numCell}>{cell('blocked', styles.numIneligible)}</td>
                                                <td className={styles.numCell}>{cell('alreadyImported', styles.numDuplicate)}</td>
                                                <td className={styles.numCell}>{cell('unmatchedFiles')}</td>
                                                <td className={styles.statusCell}>
                                                    {importSummary(importer.imports[run.id]) ||
                                                        (counts && Number(counts.importable || 0) > 0 ? (
                                                            <span className={`${tableStyles.badge} ${styles.badgeReady}`}>Ready</span>
                                                        ) : (
                                                            <span className={styles.numMuted}>—</span>
                                                        ))}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    <div className={styles.rowActions}>
                                                        <button
                                                            type="button"
                                                            className={styles.rowButton}
                                                            onClick={() => void importer.previewBatches([run.id])}
                                                            disabled={importBusy || !importer.dataPath.trim()}
                                                            aria-label={`${previewLabel} for ${batchLabel(run)}`}
                                                            title={
                                                                state?.status === 'failed'
                                                                    ? `Preview failed: ${state.error}`
                                                                    : 'Preview the import for this batch only'
                                                            }
                                                        >
                                                            {previewLabel}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {importer.importing ? (
                            <button type="button" onClick={importer.stop}>
                                Stop after the current batch
                            </button>
                        ) : (
                            <button type="button" onClick={startImport} disabled={importBusy || !previewsReady || !importTargets.length}>
                                {previewsReady
                                    ? `Import ${previewTotals.importable} clients from ${importTargets.length} ${
                                          importTargets.length === 1 ? 'batch' : 'batches'
                                      }${previewTotals.waivedClients ? ` (${previewTotals.waivedClients} under waived checks)` : ''}`
                                    : 'Preview every selected batch before importing'}
                            </button>
                        )}
                        {Object.keys(importer.imports).length > 0 && !importer.importing && (
                            <button type="button" className={styles.linkButton} onClick={importer.clearResults}>
                                Clear import results
                            </button>
                        )}
                    </div>
                )}
            </section>

            <ImportedReportPanel batches={analysis.batches} ccjRule={analysis.ccjRule} />

            {openClient && (
                <AnalysisClientModal
                    client={openClient}
                    batchId={openClient.batchId}
                    batchLabel={openClient.batchLabel}
                    onClose={() => setOpenClient(null)}
                />
            )}
        </div>
    )
}
